import {
  ChevronDownIcon,
  KebabHorizontalIcon,
  PlusIcon,
  StackIcon, 
  SyncIcon,
  TrashIcon,
} from "@primer/octicons-react";
import {
  ActionList,
  ActionMenu,
  Box,
  Heading,
  useConfirm,
} from "@primer/react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/ui/button";
import Text from "../../../components/ui/text";
import { useCommonContext } from "../../../contexts/common";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { ePageBaseStatus } from "../../../models/ePageBaseStatus";
import { actions } from "../../../state/actions/actionsWrapper";
import { RootState } from "../../../state/reducers";
import DangKyCaAnEditModal from "./DangKyCaAnEditModal";

interface ICaAnInfo {
  id: number;
  ca_an: string;
  order_idx: number;
}

interface IGroupedData {
  id: number;
  ten_truong: string;
  expanded?: boolean;
  ca_ans: ICaAnInfo[];
  order_idx?: number;
}

const GroupedByCaAn = () => {
  const { status, foCaAnTruongsVm, isShowEditModal, editingData } = useSelector(
    (x: RootState) => x.thucDonWrapper.caAnTruong
  );
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const { translate } = useCommonContext();
  const confirm = useConfirm();
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      status === ePageBaseStatus.is_not_initialization ||
      status === ePageBaseStatus.is_need_reload
    ) {
      handleReload();
    }
  }, [status]);

  const groupedDataSource: IGroupedData[] = useMemo(() => {
    if (foCaAnTruongsVm?.length > 0) {
      const filteredData = foCaAnTruongsVm.filter(
        (x) => x.dm_coso_id === dm_coso_id
      );

      // Group by school
      const grouped = filteredData.reduce(
        (acc: { [key: string]: IGroupedData }, curr) => {
          if (!acc[curr.ten_truong]) {
            acc[curr.ten_truong] = {
              id: curr.id,
              ten_truong: curr.ten_truong,
              ca_ans: [],
            };
          }

          // Check if ca_an already exists in the array
          const existingCaAn = acc[curr.ten_truong].ca_ans.find(
            (x) => x.ca_an === curr.ca_an
          );

          if (!existingCaAn) {
            acc[curr.ten_truong].ca_ans.push({
              id: curr.id,
              ca_an: curr.ca_an,
              order_idx: curr.order_idx,
            });
          }

          return acc;
        },
        {}
      );

      // Sort ca_ans by order_idx for each group
      Object.values(grouped).forEach(group => {
        group.ca_ans.sort((a, b) => a.order_idx - b.order_idx);
      });

      return Object.values(grouped);
    }
    return [];
  }, [dm_coso_id, foCaAnTruongsVm]);

  const handleReload = () => {
    dispatch(actions.thucDonWrapper.caAnTruong.LOAD_COSO_START(dm_coso_id));
  };

  const handleDelete = async (id: number) => {
    if (
      await confirm({
        content: translate("Base.Label.DeleteConfirm.Content"),
        title: translate("Base.Label.Confirm"),
        cancelButtonContent: translate("Base.Label.Close"),
        confirmButtonContent: translate("Base.Label.Delete"),
        confirmButtonType: "danger",
      })
    ) {
      dispatch(actions.thucDonWrapper.caAnTruong.DELETE_START(id));
    }
  };

  const renderGroupRow = (group: IGroupedData) => {
    return (
      <Box key={group.ten_truong}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            backgroundColor: "bg.subtle",
            borderBottom: "1px solid",
            borderColor: "border.subtle",
          }}
        >
          <ChevronDownIcon />
          <Text
            sx={{ ml: 2, fontWeight: "bold" }}
            text={group.ten_truong}
          ></Text>
        </Box>

        {group.ca_ans.map((caAnInfo) => (
          <Box
            key={caAnInfo.ca_an}
            sx={{
              display: "flex",
              alignItems: "center",
              p: 2,
              pl: 4,
              borderBottom: "1px solid",
              borderColor: "border.subtle",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                verticalAlign: "sub",
                lineHeight: 1,
                mr: 1,
              }}
            >
              <div style={{ color: "#62825D" }}>
                <StackIcon size={13} />
              </div>
            </Box>

            <Text 
              sx={{ flex: 1 }} 
              text={`${caAnInfo.ca_an}`}
            ></Text>
            <ActionMenu>
              <ActionMenu.Button
                icon={KebabHorizontalIcon}
                variant="invisible"
                sx={{ m: -1 }}
              >
                &nbsp;
              </ActionMenu.Button>
              <ActionMenu.Overlay width="auto">
                <ActionList>
                  <ActionList.Item
                    variant="danger"
                    onSelect={() => handleDelete(caAnInfo.id)}
                  >
                    <ActionList.LeadingVisual>
                      <TrashIcon />
                    </ActionList.LeadingVisual>
                    <Text text="Base.Label.Delete" />
                  </ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Heading sx={{ fontSize: "24px", m: 0 }}>
          <Text text="Danh sách ca ăn theo trường" />
        </Heading>
        <Box sx={{ display: "flex", gap: 2 }}>
        <Button
            text="Base.Label.Refresh"
            leadingVisual={SyncIcon}
            size="medium"
            onClick={handleReload}
          />
          <Button
            text="Base.Label.AddNew"
            leadingVisual={PlusIcon}
            size="medium"
            variant="primary"
            onClick={() => {
              dispatch(
                actions.thucDonWrapper.caAnTruong.SHOW_EDIT_MODAL(undefined)
              );
            }}
          />
          
        </Box>
      </Box>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "border.default",
          borderRadius: 2,
        }}
      >
        {status === ePageBaseStatus.is_loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Text text="Base.Label.Loading" />
          </Box>
        ) : groupedDataSource.length > 0 ? (
          groupedDataSource.map(renderGroupRow)
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Text text="Base.Label.NoData" />
          </Box>
        )}
      </Box>

      {isShowEditModal && <DangKyCaAnEditModal />}
    </Box>
  );
};

export default GroupedByCaAn;